import React, { useEffect, useRef, useState } from "react";
import { AutoComplete, Checkbox, Drawer, Tag } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { HiOutlineBriefcase } from "react-icons/hi";
import { TbMapPin } from "react-icons/tb";
import { MdOutlineWork } from "react-icons/md";
import {
  fetchFacilityRequest,
  fetchMyGigsKeySearchSkills,
  fetchVmsRequest,
  keySearchMyGigsLocation,
} from "../../../redux/actions";
import { DEFAULT_FILTERS } from "../constants";
import ManageJobsFilterSection from "./ManageJobsFilterSection";
import {YEARS_OF_EXP_OPTIONS} from "../../SearchSkillListing/components/AllGigs/constants";
import jobTitle from '../../../assets/icons/jobTitle.svg'
import { BILLING_ELIGIBLE_SOURCE } from "../../../shared/billing";

const safeArray = (value) => (Array.isArray(value) ? value : []);

const getStoredProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("userProfile") || "null") || {};
  } catch {
    return {};
  }
};

const getProfileId = () => {
  const profile = getStoredProfile();
  return profile?.MasterAccountId || profile?.AccountId;
};

const getSkillOption = (item) => {
  const name =
    item?._id ||
    item?.Skills?.SkillName ||
    item?.SkillName ||
    item?.skillName ||
    item?.name ||
    item?.title ||
    "";
  const id = item?.SkillId || item?.Skills?.SkillId || item?._id || name;
  return { value: name, key: String(id), label: name, queryValue: name };
};

const getLocationOption = (item) => {
  const cityName = item?.CityName || item?.name || item?.title || "";
  const displayName = item?.LocationWithCountry || cityName;
  const id = item?.CityId || item?._id || displayName;
  return {
    value: displayName,
    key: String(id),
    label: displayName,
    queryValue: displayName,
  };
};

const getVmsOption = (item) => {
  const name = item?.VMSName || item?.name || item?.title || "";
  const id = item?._id || name;
  return { value: name, key: String(id), label: name, queryValue: name };
};

const getFacilityOption = (item) => {
  const name = item?.FacilityName || item?.name || item?.title || "";
  const id = item?._id || name;
  return { value: name, key: String(id), label: name, queryValue: name };
};

const normalizeFilters = (filters = {}) => ({
  ...DEFAULT_FILTERS,
  ...filters,
  SkillsId: safeArray(filters.SkillsId),
  Skills: safeArray(filters.Skills),
  years_of_experience: safeArray(filters.years_of_experience),
  CityId: safeArray(filters.CityId),
  cities: safeArray(filters.cities),
  RegionId: safeArray(filters.RegionId),
  regions: safeArray(filters.regions),
  CountryId: safeArray(filters.CountryId),
  countries: safeArray(filters.countries),
  vms: safeArray(filters.vms),
  facility: safeArray(filters.facility),
  FacilityId: safeArray(filters.FacilityId),
  facilityNames: safeArray(filters.facilityNames),
});

export default function ManageJobsFilterDrawer({ visible, onClose, onApply, appliedFilters }) {
  const dispatch = useDispatch();
  const profile = getStoredProfile();
  const isPrivateEmployer = profile?.IsPrivateEmployer;
  const profession = profile?.Professions?.ProfessionCode;
  const userSource = String(
    profile?.Source || localStorage.getItem("cognitoSource") || ""
  )
    .trim()
    .toLowerCase();
  const canShowPtmFilters =
    String(profile?.PvtEmpName || "").trim().toLowerCase() === "sg-ptm" &&
    userSource !== BILLING_ELIGIBLE_SOURCE;
  const skillSuggestions = useSelector((state) => state.myGigsFilterReducer.skills);
  const loadingSkills = useSelector((state) => state.myGigsFilterReducer.loadingSkills);
  const locationSuggestions = useSelector((state) => safeArray(state.myGigsFilterReducer.locations));
  const vmsOptionsData = useSelector((state) => safeArray(state.vmsFilterReducer.data));
  const facilityOptionsData = useSelector((state) => safeArray(state.VmsFacilityReducer?.data));
  const loadingFacilities = useSelector((state) => state.VmsFacilityReducer?.loading);

  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [skillInput, setSkillInput] = useState("");
  const [locationInput, setLocationInput] = useState("");
  const [vmsInput, setVmsInput] = useState("");
  const [facilityInput, setFacilityInput] = useState("");
  const [expanded, setExpanded] = useState({ job: true, skills: true, location: true });

  const skillSearchRef = useRef(
    _.debounce((value, currentFilters) => {
      dispatch(
        fetchMyGigsKeySearchSkills({
          value: value ? `skill=${encodeURIComponent(value)}` : null,
          body: {
            ...DEFAULT_FILTERS,
            ...currentFilters,
            all: "",
            skill: value || "",
            // MygigsProfileId: getProfileId(),
          },
        })
      );
    }, 400)
  );

  const facilitySearchRef = useRef(
    _.debounce((value, selectedVms) => {
      dispatch(
        fetchFacilityRequest({
          search: value || "",
          vms: safeArray(selectedVms),
        })
      );
    }, 400)
  );

  const locationSearchRef = useRef(
    _.debounce((value) => {
      dispatch(
        keySearchMyGigsLocation({
          value: value ? `location=${encodeURIComponent(value)}` : "location",
          body: {
            isPrivateGig: false,
            all: "",
            // MygigsProfileId: getProfileId(),
          },
        })
      );
    }, 400)
  );

  useEffect(() => {
    if (!visible) return;
    const nextFilters = normalizeFilters(appliedFilters);
    setFilters(nextFilters);
    setSkillInput("");
    setLocationInput("");
    setVmsInput("");
    setFacilityInput("");
    if (canShowPtmFilters) {
      dispatch(fetchVmsRequest());
      if (nextFilters.vms.length) {
        dispatch(fetchFacilityRequest({ vms: nextFilters.vms }));
      }
    }
    dispatch(
      fetchMyGigsKeySearchSkills({
        value: null,
        body: { ...nextFilters, all: "", MygigsProfileId: getProfileId() },
      })
    );
    const skillSearch = skillSearchRef.current;
    const locationSearch = locationSearchRef.current;
    const facilitySearch = facilitySearchRef.current;
    return () => {
      skillSearch.cancel();
      locationSearch.cancel();
      facilitySearch.cancel();
    };
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSection = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleJobType = (value) =>
    setFilters((prev) => ({
      ...prev,
      JobOrderTypeName: prev.JobOrderTypeName === value ? null : value,
    }));

  const toggleJobStatus = (value) =>
    setFilters((prev) => ({
      ...prev,
      JobOrderStatusName: prev.JobOrderStatusName === value ? "All" : value,
    }));

  const togglePrivate = (value) =>
    setFilters((prev) => ({ ...prev, isPrivateGig: value }));

  const addSkill = (_, option) => {
    const skillName = option?.queryValue || option?.value || "";
    const id = option?.key || skillName;
    if (!skillName) return;
    setFilters((prev) => ({ ...prev, Skills: [skillName], SkillsId: [id] }));
    setSkillInput("");
  };

  const removeSkill = (skillName) =>
    setFilters((prev) => {
      const current = normalizeFilters(prev);
      return {
        ...current,
        Skills: current.Skills.filter((s) => s !== skillName),
        SkillsId: current.SkillsId.filter((_, i) => current.Skills[i] !== skillName),
      };
    });

  const addLocation = (_, option) => {
    const cityName = option?.queryValue || option?.value || "";
    const id = option?.key || cityName;
    setFilters((prev) => {
      const current = normalizeFilters(prev);
      if (!cityName || current.cities.includes(cityName)) return current;
      return { ...current, cities: [...current.cities, cityName], CityId: [...current.CityId, id] };
    });
    setLocationInput("");
  };

  const removeLocation = (cityName) =>
    setFilters((prev) => {
      const current = normalizeFilters(prev);
      return {
        ...current,
        cities: current.cities.filter((c) => c !== cityName),
        CityId: current.CityId.filter((_, i) => current.cities[i] !== cityName),
      };
    });

  const addVms = (_, option) => {
    const vmsName = option?.queryValue || option?.value || "";
    if (!vmsName) return;
    setFilters((prev) => {
      const current = normalizeFilters(prev);
      if (current.vms.includes(vmsName)) return current;
      const nextVms = [...current.vms, vmsName];
      dispatch(fetchFacilityRequest({ vms: nextVms }));
      return {
        ...current,
        vms: nextVms,
        facility: [],
        FacilityId: [],
        facilityNames: [],
      };
    });
    setVmsInput("");
  };

  const removeVms = (vmsName) =>
    setFilters((prev) => {
      const current = normalizeFilters(prev);
      const nextVms = current.vms.filter((item) => item !== vmsName);
      if (nextVms.length) {
        dispatch(fetchFacilityRequest({ vms: nextVms }));
      }
      return {
        ...current,
        vms: nextVms,
        facility: [],
        FacilityId: [],
        facilityNames: [],
      };
    });

  const addFacility = (_, option) => {
    const facilityName = option?.queryValue || option?.value || "";
    const facilityId = option?.key || "";
    if (!facilityName || !facilityId) return;
    setFilters((prev) => {
      const current = normalizeFilters(prev);
      if (current.facility.includes(facilityId)) return current;
      return {
        ...current,
        facility: [...current.facility, facilityId],
        FacilityId: [...current.FacilityId, facilityId],
        facilityNames: [...current.facilityNames, facilityName],
      };
    });
    setFacilityInput("");
  };

  const removeFacility = (facilityId) =>
    setFilters((prev) => {
      const current = normalizeFilters(prev);
      const removeIndex = current.facility.indexOf(facilityId);
      return {
        ...current,
        facility: current.facility.filter((id) => id !== facilityId),
        FacilityId: current.FacilityId.filter((id) => id !== facilityId),
        facilityNames: current.facilityNames.filter((_, index) => index !== removeIndex),
      };
    });

  const toggleExperience = (value) =>
    setFilters((prev) => {
      const normalized = normalizeFilters(prev);
      const current = normalized.years_of_experience;
      return {
        ...normalized,
        years_of_experience: current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });

  const jobCount =
    (filters.isPrivateGig ? 1 : 0) +
    (filters.JobOrderTypeName ? 1 : 0) +
    (filters.JobOrderStatusName !== "All" ? 1 : 0) +
    (canShowPtmFilters
      ? (filters.vms?.length || 0) + (filters.facility?.length || 0)
      : 0);
  const skillsCount = (filters.Skills?.length || 0) + (filters.years_of_experience?.length || 0);
  const locationCount =
    (filters.cities?.length || 0) +
    (filters.regions?.length || 0) +
    (filters.countries?.length || 0);

  const skillOptions = safeArray(skillSuggestions?.result || skillSuggestions).map(getSkillOption);
  const locationOptions = locationSuggestions.map(getLocationOption);
  const vmsOptions = vmsOptionsData
    .filter((item) =>
      String(item?.VMSName || "").toLowerCase().includes(vmsInput.toLowerCase())
    )
    .map(getVmsOption);
  const facilityOptions = facilityOptionsData.map(getFacilityOption);

  const handleReset = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setSkillInput("");
    setLocationInput("");
    setVmsInput("");
    setFacilityInput("");
    onApply({ ...DEFAULT_FILTERS });
    onClose();
  };

  const handleApply = () => {
    const nextFilters = normalizeFilters(filters);
    onApply(
      canShowPtmFilters
        ? nextFilters
        : {
            ...nextFilters,
            vms: [],
            facility: [],
            FacilityId: [],
            facilityNames: [],
          }
    );
    onClose();
  };

  const renderPtmJobFilters = () => {
    if (!canShowPtmFilters) return null;

    return (
      <>
        <div className="mj-fd-field-label" style={{ marginTop: 16 }}>
          VMS
        </div>
        <AutoComplete
          className="mj-fd-autocomplete"
          dropdownClassName="mj-fd-autocomplete-dropdown"
          options={vmsOptions}
          value={vmsInput}
          onChange={setVmsInput}
          onSearch={setVmsInput}
          onSelect={addVms}
          placeholder="Select VMS"
          notFoundContent={null}
          dropdownMatchSelectWidth
          filterOption={false}
        />
        {(filters.vms?.length || 0) > 0 && (
          <div className="mj-fd-tags">
            {(filters.vms || []).map((vmsName) => (
              <Tag
                key={vmsName}
                closable
                onClose={() => removeVms(vmsName)}
                className="mj-fd-tag"
              >
                {vmsName}
              </Tag>
            ))}
          </div>
        )}

        <div className="mj-fd-field-label" style={{ marginTop: 16 }}>
          Facility
        </div>
        <AutoComplete
          className="mj-fd-autocomplete"
          dropdownClassName="mj-fd-autocomplete-dropdown"
          options={facilityOptions}
          value={facilityInput}
          onChange={setFacilityInput}
          onSearch={(value) => {
            setFacilityInput(value);
            facilitySearchRef.current(value, filters.vms);
          }}
          onSelect={addFacility}
          placeholder="Select facility"
          notFoundContent={loadingFacilities ? "Loading..." : null}
          dropdownMatchSelectWidth
          filterOption={false}
          disabled={!(filters.vms?.length || 0)}
        />
        {(filters.facility?.length || 0) > 0 && (
          <div className="mj-fd-tags">
            {(filters.facility || []).map((facilityId, index) => (
              <Tag
                key={facilityId}
                closable
                onClose={() => removeFacility(facilityId)}
                className="mj-fd-tag"
              >
                {(filters.facilityNames || [])[index] || facilityId}
              </Tag>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <Drawer
      title={
        <div className="mj-fd-header">
          <span className="mj-fd-title">Filter</span>
          <CloseOutlined className="mj-fd-close" onClick={onClose} />
        </div>
      }
      placement="right"
      width={400}
      closable={false}
      open={visible}
      onClose={onClose}
      bodyStyle={{ padding: 0, overflowY: "auto" }}
      footer={
        <div className="mj-fd-footer">
          <button className="mj-fd-reset-btn" onClick={handleReset}>
            Reset
          </button>
          <button className="mj-fd-apply-btn" onClick={handleApply}>
            Apply Filter
          </button>
        </div>
      }
      footerStyle={{ padding: "16px 20px", borderTop: "1px solid #f0f0f0" }}
    >
      <ManageJobsFilterSection
        icon={<img src={jobTitle} alt="Job Title" />}
        title="Job"
        count={jobCount}
        expanded={expanded.job}
        onToggle={() => toggleSection("job")}
      >
        <div className="mj-fd-field-label">Job</div>
        <div className="mj-fd-chips">
          <button
            className={`mj-chip ${!filters.isPrivateGig ? "mj-chip-active" : ""}`}
            onClick={() => togglePrivate(false)}
          >
            All Jobs
          </button>
          {isPrivateEmployer && <button
            className={`mj-chip ${filters.isPrivateGig ? "mj-chip-active" : ""}`}
            onClick={() => togglePrivate(true)}
          >
            Private Jobs
          </button>}
        </div>

        <div className="mj-fd-field-label">Job Type</div>
        <div className="mj-fd-chips">
          {["Contract", "Permanent", ...(profession === "HC" ? ["Per-Diem"] : [])].map((type) => (
            <button
              key={type}
              className={`mj-chip ${filters.JobOrderTypeName === type ? "mj-chip-active" : ""}`}
              onClick={() => toggleJobType(type)}
            >
              {type}
            </button>
          ))}
        </div>

        <div className="mj-fd-field-label">Job Status</div>
        <div className="mj-fd-chips">
          {["All", "Published", "Expired", "Deleted"].map((status) => (
            <button
              key={status}
              className={`mj-chip ${
                filters.JobOrderStatusName === status ? "mj-chip-active" : ""
              }`}
              onClick={() => toggleJobStatus(status)}
            >
              {status}
            </button>
          ))}
        </div>
        {renderPtmJobFilters()}
      </ManageJobsFilterSection>

      <div className="mj-fd-divider" />

      <ManageJobsFilterSection
        icon={<HiOutlineBriefcase className="mj-fd-section-icon" />}
        title="Skills & Experience"
        count={skillsCount}
        expanded={expanded.skills}
        onToggle={() => toggleSection("skills")}
      >
        <div className="mj-fd-field-label">Skill</div>
        <AutoComplete
          className="mj-fd-autocomplete"
          options={skillOptions}
          value={skillInput}
          onChange={setSkillInput}
          onSearch={(value) => {
            setSkillInput(value);
            skillSearchRef.current(value, filters);
          }}
          onSelect={addSkill}
          placeholder="e.g. React, Python"
          notFoundContent={loadingSkills ? "Loading..." : null}
          dropdownMatchSelectWidth={false}
          filterOption={false}
        />
        {(filters.Skills?.length || 0) > 0 && (
          <div className="mj-fd-tags">
            <Tag
              closable
              onClose={() => removeSkill(filters.Skills[0])}
              className="mj-fd-tag"
            >
              {filters.Skills[0]}
            </Tag>
          </div>
        )}

        <div className="mj-fd-field-label" style={{ marginTop: 16 }}>
          Years of Experience
        </div>
        <div className="mj-fd-exp-grid">
          {YEARS_OF_EXP_OPTIONS.map((item) => (
            <div key={item.value} className="mj-fd-exp-item">
              <Checkbox
                checked={(filters.years_of_experience || []).includes(item.value)}
                onChange={() => toggleExperience(item.value)}
              >
                {item.label}
              </Checkbox>
            </div>
          ))}
        </div>
      </ManageJobsFilterSection>

      <div className="mj-fd-divider" />

      <ManageJobsFilterSection
        icon={<TbMapPin className="mj-fd-section-icon" />}
        title="Location"
        count={locationCount}
        expanded={expanded.location}
        onToggle={() => toggleSection("location")}
      >
        <div className="mj-fd-field-label">Location</div>
        <AutoComplete
          className="mj-fd-autocomplete"
          options={locationOptions}
          value={locationInput}
          onChange={setLocationInput}
          onSearch={(value) => {
            setLocationInput(value);
            locationSearchRef.current(value);
          }}
          onSelect={addLocation}
          placeholder="e.g. San Francisco"
          notFoundContent={null}
          dropdownMatchSelectWidth={false}
          filterOption={false}
        />
        {(filters.cities?.length || 0) > 0 && (
          <div className="mj-fd-tags">
            {(filters.cities || []).map((city) => (
              <Tag
                key={city}
                closable
                onClose={() => removeLocation(city)}
                className="mj-fd-tag"
              >
                {city}
              </Tag>
            ))}
          </div>
        )}
      </ManageJobsFilterSection>
    </Drawer>
  );
}
